import { Field, Rule, RuleGroup, DEFAULT_OPERATORS, Translations, DEFAULT_TRANSLATIONS, APIFieldConfig, LoadFieldsOptions } from '../core/model';
import { styles } from '../styles/main.css';

// Cache entry with optional TTL
interface CacheEntry {
    results: Record<string, string>;
    timestamp: number;
}

export class QueryBuilder extends HTMLElement {
    private _fields: Field[] = [];
    private _value: RuleGroup = { id: 'root', condition: 'AND', rules: [] };
    private _serializer: ((value: RuleGroup) => any) | null = null;

    private _translations: Translations = DEFAULT_TRANSLATIONS;
    private _direction: 'ltr' | 'rtl' = 'ltr';
    
    // Cache for search results: Map<fieldId:searchTerm, CacheEntry>
    private _searchCache: Map<string, CacheEntry> = new Map();
    
    // Track which rule's value input should be focused after render
    private _focusRuleId: string | null = null;

    static get observedAttributes() {
        return ['direction'];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === 'direction' && oldValue !== newValue) {
            this._direction = newValue as 'ltr' | 'rtl';
            this.render();
        }
    }

    get fields(): Field[] {
        return this._fields;
    }

    set fields(value: Field[]) {
        this._fields = value;
        this.render();
    }

    get value(): RuleGroup {
        return this._value;
    }

    set value(val: RuleGroup) {
        this._value = val;
        this.render();
    }

    private generateId(): string {
        return Math.random().toString(36).substr(2, 9);
    }

    private createRule(): Rule {
        const firstField = this._fields[0];
        return {
            id: this.generateId(),
            field: firstField ? firstField.id : '',
            operator: 'equal',
            value: ''
        };
    }

    private createGroup(): RuleGroup {
        return {
            id: this.generateId(),
            condition: 'AND',
            rules: []
        };
    }

    private _parser: ((data: any) => RuleGroup) | null = null;

    get serializer(): ((value: RuleGroup) => any) | null {
        return this._serializer;
    }

    set serializer(fn: ((value: RuleGroup) => any) | null) {
        this._serializer = fn;
    }

    get parser(): ((data: any) => RuleGroup) | null {
        return this._parser;
    }

    set parser(fn: ((data: any) => RuleGroup) | null) {
        this._parser = fn;
    }

    get translations(): Translations {
        return this._translations;
    }

    set translations(value: Translations) {
        this._translations = { ...DEFAULT_TRANSLATIONS, ...value };
        this.render();
    }

    get direction(): 'ltr' | 'rtl' {
        return this._direction;
    }

    set direction(value: 'ltr' | 'rtl') {
        if (this._direction === value) return;
        this.setAttribute('direction', value);
    }

    public setRules(data: any) {
        if (this._parser) {
            this._value = this._parser(data);
        } else {
            this._value = data as RuleGroup;
        }
        this.render();
        this.updateValue();
    }

    /**
     * Filter an array of items using the current query rules.
     * This performs client-side filtering - ideal for small to medium datasets.
     * 
     * @param items - Array of objects to filter
     * @returns Filtered array of items that match the current rules
     * 
     * @example
     * const items = [
     *   { name: 'John', age: 30 },
     *   { name: 'Jane', age: 25 }
     * ];
     * const filtered = qb.filterItems(items);
     */
    public filterItems<T extends Record<string, any>>(items: T[]): T[] {
        if (!items || !Array.isArray(items)) {
            console.error('filterItems: items must be an array');
            return [];
        }

        // If no rules, return all items
        if (!this._value.rules || this._value.rules.length === 0) {
            return [...items];
        }

        return items.filter(item => this.evaluateGroup(item, this._value));
    }

    /**
     * Evaluate a group of rules against an item
     */
    private evaluateGroup(item: Record<string, any>, group: RuleGroup): boolean {
        if (!group.rules || group.rules.length === 0) {
            return true;
        }

        const results = group.rules.map(ruleOrGroup => {
            if ('condition' in ruleOrGroup) {
                // It's a nested group
                return this.evaluateGroup(item, ruleOrGroup as RuleGroup);
            } else {
                // It's a rule
                return this.evaluateRule(item, ruleOrGroup as Rule);
            }
        });

        // Apply NOT modifier if present
        let result: boolean;
        if (group.condition === 'AND') {
            result = results.every(r => r);
        } else {
            result = results.some(r => r);
        }

        return group.not ? !result : result;
    }

    /**
     * Evaluate a single rule against an item
     */
    private evaluateRule(item: Record<string, any>, rule: Rule): boolean {
        const itemValue = item[rule.field];
        const ruleValue = rule.value;

        switch (rule.operator) {
            case 'equal':
                return itemValue == ruleValue;
            
            case 'not_equal':
                return itemValue != ruleValue;
            
            case 'contains':
                return String(itemValue ?? '').toLowerCase().includes(String(ruleValue ?? '').toLowerCase());
            
            case 'not_contains':
                return !String(itemValue ?? '').toLowerCase().includes(String(ruleValue ?? '').toLowerCase());
            
            case 'starts_with':
                return String(itemValue ?? '').toLowerCase().startsWith(String(ruleValue ?? '').toLowerCase());
            
            case 'ends_with':
                return String(itemValue ?? '').toLowerCase().endsWith(String(ruleValue ?? '').toLowerCase());
            
            case 'is_empty':
                return itemValue === '' || itemValue === null || itemValue === undefined;
            
            case 'is_not_empty':
                return itemValue !== '' && itemValue !== null && itemValue !== undefined;
            
            case 'is_null':
                return itemValue === null || itemValue === undefined;
            
            case 'is_not_null':
                return itemValue !== null && itemValue !== undefined;
            
            case 'greater':
                return Number(itemValue) > Number(ruleValue);
            
            case 'less':
                return Number(itemValue) < Number(ruleValue);
            
            case 'greater_or_equal':
                return Number(itemValue) >= Number(ruleValue);
            
            case 'less_or_equal':
                return Number(itemValue) <= Number(ruleValue);
            
            case 'between':
                if (Array.isArray(ruleValue) && ruleValue.length === 2) {
                    const num = Number(itemValue);
                    return num >= Number(ruleValue[0]) && num <= Number(ruleValue[1]);
                }
                return false;
            
            case 'not_between':
                if (Array.isArray(ruleValue) && ruleValue.length === 2) {
                    const num = Number(itemValue);
                    return num < Number(ruleValue[0]) || num > Number(ruleValue[1]);
                }
                return false;
            
            case 'in_array':
                if (Array.isArray(ruleValue)) {
                    return ruleValue.some(v => String(v) === String(itemValue));
                }
                return String(ruleValue) === String(itemValue);
            
            case 'not_in_array':
                if (Array.isArray(ruleValue)) {
                    return !ruleValue.some(v => String(v) === String(itemValue));
                }
                return String(ruleValue) !== String(itemValue);
            
            default:
                console.warn(`Unknown operator: ${rule.operator}`);
                return true;
        }
    }

    /**
     * Load field configuration from an API endpoint.
     * Supports:
     * - Static `values` for small lists
     * - `valuesEndpoint` to fetch values once on load
     * - `search.endpoint` for live search
     * 
     * @param url - API endpoint URL
     * @param options - Optional configuration
     */
    public async loadFieldsFromAPI(url: string, options?: LoadFieldsOptions): Promise<void> {
        const baseUrl = options?.baseUrl || '';
        
        // Fetch field configuration
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to load fields: ${response.statusText}`);
        }
        
        const apiFields: APIFieldConfig[] = await response.json();
        
        // Transform API fields to internal Field format
        const fields: Field[] = await Promise.all(
            apiFields.map(async (apiField) => {
                // Apply custom mapper if provided
                const mappedField = options?.mapper ? options.mapper(apiField) : apiField;
                
                const field: Field = {
                    id: mappedField.id,
                    label: mappedField.label,
                    type: mappedField.type,
                    operators: mappedField.operators,
                    values: mappedField.values
                };

                // Fetch values from endpoint if specified
                if (mappedField.valuesEndpoint) {
                    const valuesUrl = mappedField.valuesEndpoint.startsWith('http') 
                        ? mappedField.valuesEndpoint 
                        : baseUrl + mappedField.valuesEndpoint;
                    
                    try {
                        const valuesResponse = await fetch(valuesUrl);
                        if (valuesResponse.ok) {
                            field.values = await valuesResponse.json();
                        }
                    } catch (error) {
                        console.warn(`Failed to fetch values from ${valuesUrl}:`, error);
                    }
                }

                // Wire up search configuration
                if (mappedField.search) {
                    const searchConfig = mappedField.search;
                    
                    field.search = {
                        mode: searchConfig.mode || 'remote',
                        minChars: searchConfig.minChars,
                        debounceMs: searchConfig.debounceMs,
                        cache: searchConfig.cache,
                        hybridAlwaysFetch: searchConfig.hybridAlwaysFetch,
                        hybridFetchThreshold: searchConfig.hybridFetchThreshold,
                        placeholder: searchConfig.placeholder,
                        loadingText: searchConfig.loadingText,
                        noResultsText: searchConfig.noResultsText,
                        
                        // Wire search endpoint to onSearch callback
                        onSearch: searchConfig.onSearch || (searchConfig.endpoint 
                            ? async (term: string, fieldId: string) => {
                                const searchUrl = searchConfig.endpoint!.startsWith('http')
                                    ? searchConfig.endpoint!
                                    : baseUrl + searchConfig.endpoint!;
                                const res = await fetch(`${searchUrl}?q=${encodeURIComponent(term)}&field=${fieldId}`);
                                return res.json();
                            }
                            : async () => ({})
                        ),
                        
                        // Wire resolveLabelEndpoint if provided
                        onResolveLabel: searchConfig.resolveLabelEndpoint
                            ? async (key: string, fieldId: string) => {
                                const resolveUrl = searchConfig.resolveLabelEndpoint!.startsWith('http')
                                    ? searchConfig.resolveLabelEndpoint!
                                    : baseUrl + searchConfig.resolveLabelEndpoint!;
                                const res = await fetch(`${resolveUrl}?key=${encodeURIComponent(key)}&field=${fieldId}`);
                                const data = await res.json();
                                return data.label || key;
                            }
                            : undefined
                    };
                }

                return field;
            })
        );

        this._fields = fields;
        this.render();
    }

    private updateValue() {
        this.dispatchEvent(new CustomEvent('change', {
            detail: {
                value: this._value,
                query: this._serializer ? this._serializer(this._value) : this._value
            },
            bubbles: true,
            composed: true
        }));
    }

    private addRule(groupId: string) {
        const add = (group: RuleGroup) => {
            if (group.id === groupId) {
                group.rules.push(this.createRule());
                return true;
            }
            for (const rule of group.rules) {
                if ('condition' in rule) {
                    if (add(rule as RuleGroup)) return true;
                }
            }
            return false;
        };

        add(this._value);
        this.render();
        this.updateValue();
    }

    private addGroup(groupId: string) {
        const add = (group: RuleGroup) => {
            if (group.id === groupId) {
                group.rules.push(this.createGroup());
                return true;
            }
            for (const rule of group.rules) {
                if ('condition' in rule) {
                    if (add(rule as RuleGroup)) return true;
                }
            }
            return false;
        };

        add(this._value);
        this.render();
        this.updateValue();
    }

    private getOperators(field: Field): string[] {
        // If the field has custom operators defined, use those
        if (field.operators && field.operators.length > 0) {
            return field.operators;
        }
        
        // Otherwise, return default operators for the field type
        const typeMap: { [key: string]: string[] } = {
            string: ['equal', 'not_equal', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in_array', 'not_in_array', 'is_empty', 'is_not_empty', 'is_null', 'is_not_null'],
            number: ['equal', 'not_equal', 'less', 'less_or_equal', 'greater', 'greater_or_equal', 'between', 'not_between', 'in_array', 'not_in_array', 'is_null', 'is_not_null'],
            date: ['equal', 'not_equal', 'less', 'less_or_equal', 'greater', 'greater_or_equal', 'between', 'not_between', 'in_array', 'not_in_array', 'is_null', 'is_not_null'],
            boolean: ['equal'],
            select: ['equal', 'not_equal', 'in_array', 'not_in_array', 'is_null', 'is_not_null']
        };
        return typeMap[field.type] || ['equal', 'not_equal'];
    }

    private deleteNode(id: string) {
        const deleteFromGroup = (group: RuleGroup) => {
            const index = group.rules.findIndex(r => r.id === id);
            if (index !== -1) {
                group.rules.splice(index, 1);
                return true;
            }
            for (const rule of group.rules) {
                if ('condition' in rule) {
                    if (deleteFromGroup(rule as RuleGroup)) return true;
                }
            }
            return false;
        };

        deleteFromGroup(this._value);
        this.render();
        this.updateValue();
    }

    // --- Helpers ---

    private createAsyncSearchableSelect(
        field: Field,
        selectedValue: string,
        onChange: (val: string) => void,
        selectedKeys: string[] = []  // For multi-select: highlight already selected items
    ): HTMLElement {
        const searchConfig = field.search!;
        const minChars = searchConfig.minChars ?? 2;
        const debounceMs = searchConfig.debounceMs ?? 300;
        const mode = searchConfig.mode ?? 'remote';
        const cacheConfig = searchConfig.cache ?? true;
        const hybridAlwaysFetch = searchConfig.hybridAlwaysFetch ?? false;
        const hybridFetchThreshold = searchConfig.hybridFetchThreshold ?? 1;
        const placeholder = searchConfig.placeholder ?? 'Type to search...';
        const loadingText = searchConfig.loadingText ?? 'Searching...';
        const noResultsText = searchConfig.noResultsText ?? 'No results found';

        // Auto-wire endpoint to onSearch if onSearch is not a function
        // This handles the case where search config comes from API JSON
        const onSearch: (term: string, fieldId: string) => Promise<Record<string, string>> = 
            typeof searchConfig.onSearch === 'function' 
                ? searchConfig.onSearch 
                : (searchConfig.endpoint 
                    ? async (term: string, fieldId: string) => {
                        const endpoint = searchConfig.endpoint!;
                        const separator = endpoint.includes('?') ? '&' : '?';
                        const url = `${endpoint}${separator}q=${encodeURIComponent(term)}&field=${encodeURIComponent(fieldId)}`;
                        const response = await fetch(url);
                        return response.json();
                    }
                    : async () => ({})
                );

        const container = document.createElement('div');
        container.className = 'qb-searchable-select';

        const input = document.createElement('input');
        input.type = 'text';
        input.placeholder = placeholder;
        
        // Set initial display value from cached values or raw key
        const initialLabel = field.values?.[selectedValue] || selectedValue || '';
        input.value = initialLabel;

        let currentKey = selectedValue;
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;
        // @ts-ignore - tracking variable, assigned but not read
        let _isLoading = false;
        let currentResults: Record<string, string> = {};

        const list = document.createElement('div');
        list.className = 'qb-dropdown-list';

        // Get cache key
        const getCacheKey = (searchTerm: string) => `${field.id}:${searchTerm.toLowerCase()}`;

        // Check if cache is valid
        const getCachedResults = (searchTerm: string): Record<string, string> | null => {
            if (cacheConfig === false) return null;
            
            const cacheKey = getCacheKey(searchTerm);
            const entry = this._searchCache.get(cacheKey);
            if (!entry) return null;

            // Check TTL if it's a number
            if (typeof cacheConfig === 'number') {
                const age = Date.now() - entry.timestamp;
                if (age > cacheConfig) {
                    this._searchCache.delete(cacheKey);
                    return null;
                }
            }
            return entry.results;
        };

        // Save to cache
        const setCachedResults = (searchTerm: string, results: Record<string, string>) => {
            if (cacheConfig === false) return;
            
            const cacheKey = getCacheKey(searchTerm);
            this._searchCache.set(cacheKey, {
                results,
                timestamp: Date.now()
            });

            // Also merge into field.values for future label resolution
            if (!field.values) field.values = {};
            Object.assign(field.values, results);
        };

        // Filter local values (search in both keys and labels)
        const filterLocalValues = (searchTerm: string): Record<string, string> => {
            if (!field.values) return {};
            const lowerSearch = searchTerm.toLowerCase();
            const filtered: Record<string, string> = {};
            Object.entries(field.values).forEach(([key, label]) => {
                if (label.toLowerCase().includes(lowerSearch) || key.toLowerCase().includes(lowerSearch)) {
                    filtered[key] = label;
                }
            });
            return filtered;
        };

        // Calculate match score for sorting (higher = better match)
        const getMatchScore = (key: string, label: string, searchTerm: string): number => {
            const lowerKey = key.toLowerCase();
            const lowerLabel = label.toLowerCase();
            const term = searchTerm.toLowerCase();
            
            if (lowerKey === term) return 100;           // Exact key match
            if (lowerLabel === term) return 90;          // Exact label match
            if (lowerKey.startsWith(term)) return 70;    // Key starts with
            if (lowerLabel.startsWith(term)) return 60;  // Label starts with
            if (lowerKey.includes(term)) return 40;      // Key contains
            if (lowerLabel.includes(term)) return 30;    // Label contains
            return 0;
        };

        // Sort results by best match (exact matches first)
        const sortByBestMatch = (results: Record<string, string>, searchTerm: string): Record<string, string> => {
            const entries = Object.entries(results);
            entries.sort(([keyA, labelA], [keyB, labelB]) => {
                const scoreA = getMatchScore(keyA, labelA, searchTerm);
                const scoreB = getMatchScore(keyB, labelB, searchTerm);
                return scoreB - scoreA;
            });
            return Object.fromEntries(entries);
        };

        const renderList = (results: Record<string, string>, showLoading = false, showNoResults = false) => {
            list.innerHTML = '';

            if (showLoading) {
                const loadingItem = document.createElement('div');
                loadingItem.className = 'qb-dropdown-item qb-loading';
                loadingItem.style.cursor = 'default';
                loadingItem.style.color = '#666';
                loadingItem.textContent = loadingText;
                list.appendChild(loadingItem);
                return;
            }

            const entries = Object.entries(results);
            if (entries.length === 0 && showNoResults) {
                const noResultsItem = document.createElement('div');
                noResultsItem.className = 'qb-dropdown-item';
                noResultsItem.style.cursor = 'default';
                noResultsItem.style.color = '#999';
                noResultsItem.textContent = noResultsText;
                list.appendChild(noResultsItem);
                return;
            }

            entries.forEach(([key, label]) => {
                const item = document.createElement('div');
                item.className = 'qb-dropdown-item';
                if (key === currentKey) item.classList.add('selected');
                // Mark items that are already selected in multi-select mode
                if (selectedKeys.includes(key)) {
                    item.classList.add('already-selected');
                }
                item.textContent = label;
                item.onclick = (e) => {
                    e.stopPropagation();
                    currentKey = key;
                    input.value = label;
                    list.classList.remove('show');
                    
                    // Cache the selected value for future label resolution
                    if (!field.values) field.values = {};
                    field.values[key] = label;
                    
                    onChange(key);
                };
                list.appendChild(item);
            });
        };

        const performSearch = async (searchTerm: string) => {
            // Check cache first
            const cached = getCachedResults(searchTerm);
            if (cached) {
                currentResults = sortByBestMatch(cached, searchTerm);
                renderList(currentResults, false, Object.keys(currentResults).length === 0);
                return;
            }

            // Handle different modes
            if (mode === 'local') {
                // Local only - no API call
                currentResults = sortByBestMatch(filterLocalValues(searchTerm), searchTerm);
                renderList(currentResults, false, Object.keys(currentResults).length === 0);
                return;
            }

            if (mode === 'hybrid') {
                const localResults = filterLocalValues(searchTerm);
                const localCount = Object.keys(localResults).length;
                
                // Show local results immediately (sorted)
                if (localCount > 0) {
                    currentResults = sortByBestMatch(localResults, searchTerm);
                    renderList(currentResults);
                }
                
                // Determine if we should also fetch from backend
                const shouldFetch = hybridAlwaysFetch || localCount < hybridFetchThreshold;
                
                if (shouldFetch) {
                    // If no local results, show loading
                    if (localCount === 0) {
                        _isLoading = true;
                        renderList({}, true);
                    }
                    
                    try {
                        const remoteResults = await onSearch(searchTerm, field.id);
                        // Merge local and remote, with remote overwriting any duplicates
                        const merged = { ...localResults, ...remoteResults };
                        currentResults = sortByBestMatch(merged, searchTerm);
                        setCachedResults(searchTerm, currentResults);
                        renderList(currentResults, false, Object.keys(currentResults).length === 0);
                    } catch (error) {
                        console.warn('Backend search failed, using local results only');
                        // If we had local results, they're already displayed
                        if (localCount === 0) {
                            const errorItem = document.createElement('div');
                            errorItem.className = 'qb-dropdown-item qb-error';
                            errorItem.style.cursor = 'default';
                            errorItem.style.color = '#dc3545';
                            errorItem.textContent = 'Error loading results';
                            list.innerHTML = '';
                            list.appendChild(errorItem);
                        }
                    } finally {
                        _isLoading = false;
                    }
                }
                return;
            }

            // Remote search
            _isLoading = true;
            renderList({}, true);

            try {
                const results = await onSearch(searchTerm, field.id);
                currentResults = results;
                setCachedResults(searchTerm, results);
                renderList(results, false, Object.keys(results).length === 0);
            } catch (error) {
                console.error('Search error:', error);
                const errorItem = document.createElement('div');
                errorItem.className = 'qb-dropdown-item qb-error';
                errorItem.style.cursor = 'default';
                errorItem.style.color = '#dc3545';
                errorItem.textContent = 'Error loading results';
                list.innerHTML = '';
                list.appendChild(errorItem);
            } finally {
                _isLoading = false;
            }
        };

        // Input events
        input.onfocus = () => {
            // Show local values on focus if available
            if (field.values && Object.keys(field.values).length > 0) {
                currentResults = field.values;
                renderList(field.values);
            } else if (input.value.length >= minChars) {
                performSearch(input.value);
            } else {
                const hintItem = document.createElement('div');
                hintItem.className = 'qb-dropdown-item';
                hintItem.style.cursor = 'default';
                hintItem.style.color = '#999';
                hintItem.textContent = `Type at least ${minChars} characters...`;
                list.innerHTML = '';
                list.appendChild(hintItem);
            }
            list.classList.add('show');
        };

        input.oninput = (e) => {
            const searchTerm = (e.target as HTMLInputElement).value;

            // Clear previous debounce
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            if (searchTerm.length < minChars) {
                // Show hint
                const hintItem = document.createElement('div');
                hintItem.className = 'qb-dropdown-item';
                hintItem.style.cursor = 'default';
                hintItem.style.color = '#999';
                hintItem.textContent = `Type at least ${minChars} characters...`;
                list.innerHTML = '';
                list.appendChild(hintItem);
                list.classList.add('show');
                return;
            }

            // Debounce the search
            debounceTimer = setTimeout(() => {
                performSearch(searchTerm);
            }, debounceMs);

            list.classList.add('show');
        };

        // Keyboard navigation
        let highlightedIndex = -1;
        
        input.onkeydown = (e: KeyboardEvent) => {
            const items = list.querySelectorAll('.qb-dropdown-item:not(.qb-loading):not(.qb-error)') as NodeListOf<HTMLElement>;
            const visibleItems = Array.from(items).filter(item => item.style.cursor !== 'default');
            
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!list.classList.contains('show')) {
                    list.classList.add('show');
                    return;
                }
                highlightedIndex = Math.min(highlightedIndex + 1, visibleItems.length - 1);
                updateHighlight(visibleItems);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                highlightedIndex = Math.max(highlightedIndex - 1, 0);
                updateHighlight(visibleItems);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < visibleItems.length) {
                    visibleItems[highlightedIndex].click();
                    highlightedIndex = -1;
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                list.classList.remove('show');
                input.value = field.values?.[currentKey] || currentKey || '';
                highlightedIndex = -1;
            }
        };

        const updateHighlight = (items: HTMLElement[]) => {
            items.forEach((item, idx) => {
                if (idx === highlightedIndex) {
                    item.classList.add('highlighted');
                    item.scrollIntoView({ block: 'nearest' });
                } else {
                    item.classList.remove('highlighted');
                }
            });
        };

        // Close on click outside
        const closeHandler = (e: MouseEvent) => {
            if (!document.body.contains(container)) {
                document.removeEventListener('click', closeHandler);
                return;
            }
            if (!container.contains(e.target as Node)) {
                list.classList.remove('show');
                // Reset input to current selected label
                input.value = field.values?.[currentKey] || currentKey || '';
            }
        };
        document.addEventListener('click', closeHandler);

        container.appendChild(input);
        container.appendChild(list);

        return container;
    }

    private createSearchableSelect(
        values: Record<string, string>,
        selectedValue: string,
        onChange: (val: string) => void,
        placeholder: string = 'Select value...',
        selectedKeys: string[] = []  // For multi-select: highlight already selected items
    ): HTMLElement {
        const container = document.createElement('div');
        container.className = 'qb-searchable-select';

        const input = document.createElement('input');
        input.type = 'text';
        input.placeholder = placeholder;
        // Set initial display value
        input.value = values[selectedValue] || selectedValue || '';

        // Store the actual value key in a data attribute or closure
        let currentKey = selectedValue;

        const list = document.createElement('div');
        list.className = 'qb-dropdown-list';

        const renderList = (filter: string = '') => {
            list.innerHTML = '';
            const lowerFilter = filter.toLowerCase();
            let hasResults = false;

            Object.entries(values).forEach(([key, label]) => {
                const labelStr = label ?? key; // Fallback to key if label is null/undefined
                if (labelStr.toLowerCase().includes(lowerFilter)) {
                    hasResults = true;
                    const item = document.createElement('div');
                    item.className = 'qb-dropdown-item';
                    if (key === currentKey) item.classList.add('selected');
                    // Mark items that are already selected in multi-select mode
                    if (selectedKeys.includes(key)) {
                        item.classList.add('already-selected');
                    }
                    item.textContent = labelStr;
                    item.onclick = (e) => {
                        e.stopPropagation();
                        currentKey = key;
                        input.value = labelStr;
                        list.classList.remove('show');
                        onChange(key);
                    };
                    list.appendChild(item);
                }
            });

            if (!hasResults) {
                const item = document.createElement('div');
                item.className = 'qb-dropdown-item';
                item.style.cursor = 'default';
                item.style.color = '#999';
                item.textContent = 'No results found';
                list.appendChild(item);
            }
        };

        // Input events
        input.onfocus = () => {
            renderList();
            list.classList.add('show');
        };

        input.oninput = (e) => {
            const val = (e.target as HTMLInputElement).value;
            renderList(val);
            list.classList.add('show');
            highlightedIndex = -1; // Reset highlight when typing
        };

        // Keyboard navigation
        let highlightedIndex = -1;
        
        input.onkeydown = (e: KeyboardEvent) => {
            const items = list.querySelectorAll('.qb-dropdown-item') as NodeListOf<HTMLElement>;
            const visibleItems = Array.from(items).filter(item => item.style.cursor !== 'default');
            
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!list.classList.contains('show')) {
                    renderList();
                    list.classList.add('show');
                    return;
                }
                highlightedIndex = Math.min(highlightedIndex + 1, visibleItems.length - 1);
                updateHighlight(visibleItems);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                highlightedIndex = Math.max(highlightedIndex - 1, 0);
                updateHighlight(visibleItems);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < visibleItems.length) {
                    visibleItems[highlightedIndex].click();
                    highlightedIndex = -1;
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                list.classList.remove('show');
                input.value = values[currentKey] || currentKey || '';
                highlightedIndex = -1;
            }
        };

        const updateHighlight = (items: HTMLElement[]) => {
            items.forEach((item, idx) => {
                if (idx === highlightedIndex) {
                    item.classList.add('highlighted');
                    item.scrollIntoView({ block: 'nearest' });
                } else {
                    item.classList.remove('highlighted');
                }
            });
        };

        // Close on click outside
        const closeHandler = (e: MouseEvent) => {
            if (!container.contains(e.target as Node)) {
                list.classList.remove('show');
                // Reset input to current selected label if not changed
                input.value = values[currentKey] || currentKey || '';
            }
        };
        document.addEventListener('click', closeHandler);

        // Cleanup listener when element removed (not perfect in vanilla without disconnect, but ok for now)
        // Ideally we'd use disconnectedCallback but these are created dynamically.
        // We'll rely on the fact that if container is removed, it won't be clicked.
        // But the document listener persists. This is a minor leak in this simple implementation.
        // To fix, we could attach/detach in connected/disconnected of the main component, 
        // but managing specific handlers for dynamic elements is tricky.
        // A simple fix: check if container is still in DOM inside handler.
        const safeCloseHandler = (e: MouseEvent) => {
            if (!document.body.contains(container)) {
                document.removeEventListener('click', safeCloseHandler);
                return;
            }
            closeHandler(e);
        };
        document.removeEventListener('click', closeHandler); // Remove the unsafe one
        document.addEventListener('click', safeCloseHandler);

        container.appendChild(input);
        container.appendChild(list);

        return container;
    }

    // --- Rendering ---

    private render() {
        if (!this.shadowRoot) return;

        // Apply direction
        this.setAttribute('dir', this._direction);


        const styleEl = document.createElement('style');
        styleEl.textContent = styles;

        const container = document.createElement('div');
        container.className = 'qb-container';

        // Pass translations to renderGroup
        const rootGroup = this.renderGroup(this._value);
        container.appendChild(rootGroup);

        this.shadowRoot.innerHTML = '';
        this.shadowRoot.appendChild(styleEl);
        this.shadowRoot.appendChild(container);
        
        // Auto-focus value input if requested
        if (this._focusRuleId) {
            requestAnimationFrame(() => {
                const ruleEl = this.shadowRoot?.querySelector(`.qb-rule[data-id="${this._focusRuleId}"]`);
                if (ruleEl) {
                    // Find the first input in the value group
                    const valueInput = ruleEl.querySelector('.qb-value-group input, .qb-value-group .qb-searchable-select input') as HTMLInputElement;
                    if (valueInput) {
                        valueInput.focus();
                    }
                }
                this._focusRuleId = null;
            });
        }
    }

    private renderGroup(group: RuleGroup): HTMLElement {
        const groupEl = document.createElement('div');
        groupEl.className = 'qb-group';
        groupEl.dataset.id = group.id;

        // Header
        const header = document.createElement('div');
        header.className = 'qb-group-header';

        // Condition Select (AND/OR)
        const conditionSelect = document.createElement('select');
        conditionSelect.className = 'qb-condition-select';

        // Use translations for logical operators
        const logicalOps = this._translations.logicalOperators;
        Object.keys(logicalOps).forEach(op => {
            const option = document.createElement('option');
            option.value = op;
            option.textContent = logicalOps[op];
            if (op === group.condition) option.selected = true;
            conditionSelect.appendChild(option);
        });

        conditionSelect.onchange = (e) => {
            group.condition = (e.target as HTMLSelectElement).value as 'AND' | 'OR';
            this.updateValue();
        };
        header.appendChild(conditionSelect);

        // Add Rule Button
        const addRuleBtn = document.createElement('button');
        addRuleBtn.className = 'qb-btn qb-btn-primary';
        addRuleBtn.textContent = this._translations.addRule;
        addRuleBtn.onclick = () => this.addRule(group.id);
        header.appendChild(addRuleBtn);

        // Add Group Button
        const addGroupBtn = document.createElement('button');
        addGroupBtn.className = 'qb-btn';
        addGroupBtn.textContent = this._translations.addGroup;
        addGroupBtn.onclick = () => this.addGroup(group.id);
        header.appendChild(addGroupBtn);

        // Delete Group Button (if not root)
        if (group.id !== 'root') {
            const deleteGroupBtn = document.createElement('button');
            deleteGroupBtn.className = 'qb-btn qb-btn-danger';
            deleteGroupBtn.textContent = this._translations.deleteGroup;
            deleteGroupBtn.onclick = () => this.deleteNode(group.id);
            header.appendChild(deleteGroupBtn);
        }

        groupEl.appendChild(header);

        // Rules Container
        const rulesContainer = document.createElement('div');
        rulesContainer.className = 'qb-rules-container';

        group.rules.forEach(ruleOrGroup => {
            if ('condition' in ruleOrGroup) {
                rulesContainer.appendChild(this.renderGroup(ruleOrGroup as RuleGroup));
            } else {
                rulesContainer.appendChild(this.renderRule(ruleOrGroup as Rule));
            }
        });

        groupEl.appendChild(rulesContainer);

        return groupEl;
    }

    private renderRule(rule: Rule): HTMLElement {
        const ruleEl = document.createElement('div');
        ruleEl.className = 'qb-rule';
        ruleEl.dataset.id = rule.id;

        // Field Select
        const fieldSelect = document.createElement('select');
        fieldSelect.className = 'qb-field-select';
        this._fields.forEach(field => {
            const option = document.createElement('option');
            option.value = field.id;
            option.textContent = field.label;
            if (field.id === rule.field) option.selected = true;
            fieldSelect.appendChild(option);
        });

        fieldSelect.onchange = (e) => {
            const newFieldId = (e.target as HTMLSelectElement).value;
            const newField = this._fields.find(f => f.id === newFieldId);
            if (newField) {
                rule.field = newFieldId;
                // Reset operator and value on field change
                const operators = this.getOperators(newField);
                rule.operator = operators[0];
                rule.value = '';
                this._focusRuleId = rule.id;  // Auto-focus value after render
                this.render(); // Re-render to update operator and value input
                this.updateValue();
            }
        };
        ruleEl.appendChild(fieldSelect);

        // Operator Select
        const currentField = this._fields.find(f => f.id === rule.field);
        if (currentField) {
            const operatorSelect = document.createElement('select');
            operatorSelect.className = 'qb-operator-select';
            const operators = this.getOperators(currentField);

            operators.forEach(op => {
                const option = document.createElement('option');
                option.value = op;
                // Use translations for operators
                option.textContent = this._translations.operators[op] || DEFAULT_OPERATORS[op]?.label || op;
                if (op === rule.operator) option.selected = true;
                operatorSelect.appendChild(option);
            });

            operatorSelect.onchange = (e) => {
                rule.operator = (e.target as HTMLSelectElement).value;
                // Reset value if cardinality changes (optional, but good practice)
                rule.value = '';
                this._focusRuleId = rule.id;  // Auto-focus value after render
                this.render();
                this.updateValue();
            };
            ruleEl.appendChild(operatorSelect);

            // Value Input
            const operatorDef = DEFAULT_OPERATORS[rule.operator];

            // Create a group for Value + Delete Button to keep them together
            const valueGroup = document.createElement('div');
            valueGroup.className = 'qb-value-group';

            if (operatorDef && operatorDef.cardinality > 0) {
                const isMultiValue = ['in_array', 'not_in_array'].includes(rule.operator);

                if (isMultiValue) {
                    // Initialize value as array if needed
                    if (!Array.isArray(rule.value)) {
                        rule.value = rule.value ? [rule.value] : [];
                    }

                    const tagsContainer = document.createElement('div');
                    tagsContainer.className = 'qb-tags-container';

                    // Render existing tags
                    const renderTags = () => {
                        // Clear tags (keep input)
                        const existingTags = tagsContainer.querySelectorAll('.qb-tag');
                        existingTags.forEach(t => t.remove());

                        (rule.value as any[]).forEach((val, index) => {
                            const tag = document.createElement('span');
                            tag.className = 'qb-tag';

                            // Get label for select types
                            let label = val;
                            if (currentField.type === 'select' && currentField.values) {
                                label = currentField.values[val] || val;
                            }

                            const tagText = document.createElement('span');
                            tagText.textContent = label;
                            tag.appendChild(tagText);

                            const removeBtn = document.createElement('span');
                            removeBtn.className = 'qb-tag-remove';
                            removeBtn.textContent = this._translations.deleteRule; // Use translation for X
                            removeBtn.onclick = () => {
                                (rule.value as any[]).splice(index, 1);
                                renderTags();
                                this.updateValue();
                            };
                            tag.appendChild(removeBtn);

                            // Insert before the input
                            tagsContainer.insertBefore(tag, inputElement);
                        });
                    };

                    let inputElement: HTMLElement;

                    if (currentField.type === 'select' && (currentField.values || currentField.search)) {
                        // Use async search or static searchable select for adding tags
                        const onTagSelected = (val: string) => {
                            if (val && !(rule.value as any[]).includes(val)) {
                                (rule.value as any[]).push(val);
                                renderTags();
                                this.updateValue();
                            }
                            // Clear the input but keep focus for adding more values
                            const input = inputElement.querySelector('input') as HTMLInputElement;
                            if (input) {
                                input.value = '';
                                // Keep dropdown closed but input focused
                                const dropdown = inputElement.querySelector('.qb-dropdown-list');
                                if (dropdown) dropdown.classList.remove('show');
                                // Focus after a small delay to ensure DOM is updated
                                requestAnimationFrame(() => input.focus());
                            }
                        };

                        if (currentField.search) {
                            // Use async searchable select
                            inputElement = this.createAsyncSearchableSelect(
                                currentField,
                                '',
                                onTagSelected,
                                rule.value as string[]  // Pass selected values for highlighting
                            );
                        } else {
                            // Use static searchable select
                            inputElement = this.createSearchableSelect(
                                currentField.values!,
                                '',
                                onTagSelected,
                                'Select value...',
                                rule.value as string[]  // Pass selected values for highlighting
                            );
                        }
                        // Force input style to match tags input
                        const input = inputElement.querySelector('input');
                        if (input) input.className = 'qb-tags-input';
                        inputElement.style.width = 'auto';
                        inputElement.style.flex = '1';
                        inputElement.style.minWidth = '150px';

                    } else {
                        const input = document.createElement('input');
                        // Handle input types
                        if (currentField.type === 'number') {
                            input.type = 'number';
                        } else if (currentField.type === 'date') {
                            input.type = 'date';
                        } else {
                            input.type = 'text';
                        }

                        input.className = 'qb-tags-input';
                        input.placeholder = currentField.type === 'date' ? '' : 'Type and press Enter...'; // Could be translated

                        // Handler for Enter key
                        input.onkeydown = (e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                const val = (e.target as HTMLInputElement).value.trim();
                                if (val) {
                                    (rule.value as any[]).push(val);
                                    renderTags();
                                    this.updateValue();
                                    (e.target as HTMLInputElement).value = '';
                                }
                            }
                        };
                        inputElement = input;
                    }

                    tagsContainer.appendChild(inputElement);
                    renderTags(); // Initial render
                    valueGroup.appendChild(tagsContainer);

                } else if (operatorDef.cardinality === 2) {
                    // Between (2 inputs)
                    if (!Array.isArray(rule.value)) {
                        rule.value = ['', ''];
                    }

                    const container = document.createElement('div');
                    container.style.display = 'flex';
                    container.style.gap = '0.5rem';
                    container.style.alignItems = 'center';
                    container.style.flex = '1'; // Ensure it takes space

                    const createInput = (index: number, placeholder: string) => {
                        const input = document.createElement('input');
                        input.type = 'text'; // Default
                        if (currentField.type === 'number') input.type = 'number';
                        if (currentField.type === 'date') input.type = 'date';
                        input.style.width = '100%'; // Full width in flex item

                        input.placeholder = placeholder;
                        input.value = rule.value[index] || '';
                        input.oninput = (e) => {
                            const newVal = [...(rule.value as string[])];
                            newVal[index] = (e.target as HTMLInputElement).value;
                            rule.value = newVal;
                            this.updateValue();
                        };
                        return input;
                    };

                    container.appendChild(createInput(0, 'From')); // Could be translated
                    container.appendChild(document.createTextNode(' - '));
                    container.appendChild(createInput(1, 'To')); // Could be translated

                    valueGroup.appendChild(container);

                } else if (currentField.type === 'select' && (currentField.values || currentField.search)) {
                    // Single Select - Use Async Search or Static Searchable Select
                    let select: HTMLElement;
                    
                    if (currentField.search) {
                        // Use async searchable select
                        select = this.createAsyncSearchableSelect(
                            currentField,
                            rule.value,
                            (val) => {
                                rule.value = val;
                                this.updateValue();
                            }
                        );
                    } else {
                        // Use static searchable select
                        select = this.createSearchableSelect(
                            currentField.values!,
                            rule.value,
                            (val) => {
                                rule.value = val;
                                this.updateValue();
                            }
                        );
                    }
                    select.style.flex = '1';
                    valueGroup.appendChild(select);
                } else {
                    // Standard Input
                    const input = document.createElement('input');
                    input.type = 'text'; // Default
                    if (currentField.type === 'number') input.type = 'number';
                    if (currentField.type === 'date') input.type = 'date';
                    input.style.flex = '1';

                    input.value = rule.value;
                    input.oninput = (e) => {
                        rule.value = (e.target as HTMLInputElement).value;
                        this.updateValue();
                    };
                    valueGroup.appendChild(input);
                }
            }

            // Delete Rule Button
            const deleteBtn = document.createElement('button');
            deleteBtn.className = 'qb-btn qb-btn-danger';
            deleteBtn.textContent = this._translations.deleteRule;
            deleteBtn.onclick = () => {
                this.deleteNode(rule.id);
            };
            valueGroup.appendChild(deleteBtn);

            ruleEl.appendChild(valueGroup);
        }

        return ruleEl;
    }
}

customElements.define('abt-query-builder', QueryBuilder);
